import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { getRoleColor, getInitials } from "@/lib/roleColors";
import { formatDistanceToNow } from "date-fns";
import { AssignmentStatusBadge } from "./AssignmentStatusBadge";

export interface Assignee {
  id: string;
  full_name: string;
  member_role: string;
  assignment_status: string;
  assigned_at: string;
  accepted_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  email?: string;
  phone?: string;
}

interface AssigneeAvatarGroupProps {
  assignees: Assignee[];
  maxVisible?: number;
  size?: "small" | "medium" | "large";
  showTooltip?: boolean;
  showStatus?: boolean;
}

const AVATAR_SIZES = {
  small: "h-6 w-6 text-xs",
  medium: "h-8 w-8 text-sm",
  large: "h-10 w-10 text-base",
};

const BORDER_SIZES = {
  small: "border-2",
  medium: "border-2",
  large: "border-[3px]",
};

export function AssigneeAvatarGroup({
  assignees,
  maxVisible = 3,
  size = "medium",
  showTooltip = true,
  showStatus = true,
}: AssigneeAvatarGroupProps) {
  if (!assignees || assignees.length === 0) {
    return (
      <Badge variant="outline" className="text-muted-foreground border-dashed">
        Unassigned
      </Badge>
    );
  }

  const visibleAssignees = assignees.slice(0, maxVisible);
  const remainingCount = assignees.length - maxVisible;

  return (
    <TooltipProvider>
      <div className="flex items-center gap-1">
        <span className="text-sm text-muted-foreground mr-1">Assigned to:</span>
        <div className="flex items-center -space-x-2">
          {visibleAssignees.map((assignee) => {
            const roleColors = getRoleColor(assignee.member_role);
            const avatarSize = AVATAR_SIZES[size];
            const borderSize = BORDER_SIZES[size];

            const avatarContent = (
              <Avatar
                className={`${avatarSize} ${borderSize} cursor-pointer hover:scale-110 transition-transform`}
                style={{
                  borderColor: roleColors.border,
                  backgroundColor: roleColors.bg,
                }}
              >
                <AvatarFallback
                  style={{
                    backgroundColor: roleColors.bg,
                    color: roleColors.text,
                  }}
                >
                  {getInitials(assignee.full_name)}
                </AvatarFallback>
              </Avatar>
            );

            if (!showTooltip) {
              return <div key={assignee.id}>{avatarContent}</div>;
            }

            return (
              <Tooltip key={assignee.id}>
                <TooltipTrigger asChild>{avatarContent}</TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <div className="space-y-2">
                    <div>
                      <p className="font-semibold">{assignee.full_name}</p>
                      <Badge
                        variant="outline"
                        className="mt-1"
                        style={{
                          borderColor: roleColors.border,
                          color: roleColors.text,
                        }}
                      >
                        {roleColors.name}
                      </Badge>
                    </div>
                    
                    {showStatus && (
                      <div className="pt-2 border-t">
                        <AssignmentStatusBadge
                          status={assignee.assignment_status as any}
                          size="small"
                        />
                      </div>
                    )}

                    {assignee.email && (
                      <p className="text-xs text-muted-foreground">
                        📧 {assignee.email}
                      </p>
                    )}

                    <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
                      <p>
                        Assigned:{" "}
                        {formatDistanceToNow(new Date(assignee.assigned_at), {
                          addSuffix: true,
                        })}
                      </p>
                      {assignee.accepted_at && (
                        <p>
                          Accepted:{" "}
                          {formatDistanceToNow(new Date(assignee.accepted_at), {
                            addSuffix: true,
                          })}
                        </p>
                      )}
                      {assignee.started_at && (
                        <p>
                          Started:{" "}
                          {formatDistanceToNow(new Date(assignee.started_at), {
                            addSuffix: true,
                          })}
                        </p>
                      )}
                      {assignee.completed_at && (
                        <p>
                          Completed:{" "}
                          {formatDistanceToNow(new Date(assignee.completed_at), {
                            addSuffix: true,
                          })}
                        </p>
                      )}
                      {!assignee.accepted_at && (
                        <p className="text-yellow-600">Not yet accepted</p>
                      )}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })}

          {remainingCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className={`${AVATAR_SIZES[size]} ${BORDER_SIZES[size]} rounded-full bg-muted border-border flex items-center justify-center cursor-pointer hover:scale-110 transition-transform`}
                >
                  <span className="text-xs font-medium text-muted-foreground">
                    +{remainingCount}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <div className="space-y-1">
                  <p className="font-semibold mb-2">
                    {remainingCount} more assignee{remainingCount !== 1 ? "s" : ""}
                  </p>
                  {assignees.slice(maxVisible).map((assignee) => (
                    <p key={assignee.id} className="text-sm">
                      • {assignee.full_name} ({getRoleColor(assignee.member_role).name})
                    </p>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
